/**
 * ResourcesHub — dense "resources you didn't know existed" section for
 * the public homepage. Three columns targeting the three ICPs:
 *   1. Property managers
 *   2. Landlords
 *   3. Tenants
 *
 * Every link is a live route (no dead links, no vaporware). This is a
 * standalone section placed between the Free Tools grid and the
 * footer. Mobile-first.
 */
import { Link } from "react-router-dom";
import { Briefcase, Home, User, ExternalLink, ArrowRight, BookOpen } from "lucide-react";

const GROUPS = [
  {
    label: "For property managers",
    icon: Briefcase,
    accent: "sky",
    testid: "resources-pm",
    items: [
      { title: "Bulk rent-increase renewer", body: "One-click notices for every lease expiring in 90 days.", href: "/renewals/batch" },
      { title: "Portfolio migration from Buildium", body: "5-minute CSV import with one-click undo.", href: "/migration/buildium/how-to" },
      { title: "Vendor directory", body: "Vetted trades by province + trade for work orders.", href: "/vendors" },
      { title: "MLS listings feed", body: "Real-time Repliers-backed listings across Canada.", href: "/mls" },
      { title: "Provincial compliance library", body: "LTB · RTB · RTDRS · TAL · every RTA cheatsheet.", href: "/laws" },
      { title: "Property management software: Canada", body: "How Central Rentals compares to every US-first alternative.", href: "/best-property-management-software-canada" },
    ],
  },
  {
    label: "For landlords",
    icon: Home,
    accent: "emerald",
    testid: "resources-landlord",
    items: [
      { title: "Rent-Increase Notice Generator", body: "Correct provincial form + statutory cap in 10s.", href: "/tools/rent-increase-notice" },
      { title: "N4 / Late-Rent Notice Generator", body: "Arrears notices for every province with cure period.", href: "/tools/n4-late-rent-notice" },
      { title: "ROI calculator", body: "Cap rate, cash-on-cash, and CRA T776 planning per property.", href: "/tools/roi-calculator" },
      { title: "Rental affordability calculator", body: "Prove income multiple to a co-signer or bank.", href: "/tools/rent-affordability" },
      { title: "Landlord laws by province", body: "Notice periods, deposit limits, tribunal fees — all 13 provinces.", href: "/laws" },
      { title: "CRA T776 filing for landlords", body: "Line-by-line guide + downloadable checklist.", href: "/answers/t776-cra-filing-for-landlords" },
    ],
  },
  {
    label: "For tenants",
    icon: User,
    accent: "amber",
    testid: "resources-tenant",
    items: [
      { title: "Tenant rights by province", body: "Read your rights before you sign anything.", href: "/answers" },
      { title: "Rental affordability calculator", body: "See how much rent your income supports comfortably.", href: "/tools/rent-affordability" },
      { title: "Rent-price sanity check", body: "Is this listing overpriced for the neighborhood? Find out.", href: "/tools/rent-affordability" },
      { title: "How to dispute a rent increase", body: "Step-by-step for LTB / RTB / TAL applications.", href: "/laws" },
      { title: "Find a landlord who accepts pets", body: "Vetted pet-friendly listings across Canada.", href: "/find-a-home" },
      { title: "Report an unsafe rental", body: "Direct link to your provincial regulator + templates.", href: "/laws" },
    ],
  },
];

const ACCENTS = {
  sky:     { border: "border-sky-200",     header: "bg-sky-50 text-sky-800",         hover: "hover:border-sky-400" },
  emerald: { border: "border-emerald-200", header: "bg-emerald-50 text-emerald-800", hover: "hover:border-emerald-400" },
  amber:   { border: "border-amber-200",   header: "bg-amber-50 text-amber-800",     hover: "hover:border-amber-400" },
};

export default function ResourcesHub() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-testid="resources-hub-section">
      <div className="text-[11px] uppercase tracking-[0.22em] font-black text-slate-700 mb-2 flex items-center gap-1.5">
        <BookOpen className="w-3.5 h-3.5"/> Resources most Canadians don't know exist
      </div>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-black tracking-tight text-slate-900">
          Handpicked for property managers, landlords, and tenants.
        </h2>
        <div className="text-sm text-slate-600 max-w-md">
          Deep-linked into the exact page that answers each question — no signup wall, no scavenger hunt.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {GROUPS.map((g) => {
          const Icon = g.icon;
          const a = ACCENTS[g.accent];
          return (
            <div key={g.testid} data-testid={g.testid} className={`rounded-2xl bg-white border ${a.border} overflow-hidden shadow-sm`}>
              <div className={`px-4 py-3 flex items-center gap-2 ${a.header}`}>
                <Icon className="w-4 h-4"/>
                <div className="text-xs uppercase tracking-widest font-black">{g.label}</div>
              </div>
              <ul className="divide-y divide-slate-100">
                {g.items.map((it) => (
                  <li key={it.href + it.title}>
                    <Link
                      to={it.href}
                      data-testid={`${g.testid}-link-${it.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      className={`group flex items-start gap-3 px-4 py-3 transition-colors ${a.hover} hover:bg-slate-50`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-black text-slate-900 leading-tight">{it.title}</div>
                        <div className="text-xs text-slate-600 mt-0.5 leading-snug">{it.body}</div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-400 flex-none mt-0.5 group-hover:text-slate-900 transition-colors"/>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="mt-6 text-xs text-slate-500 flex items-center gap-1.5">
        <ExternalLink className="w-3 h-3"/> Every link opens a live page. No dead ends, no vaporware.
      </div>
    </section>
  );
}
